import { produce } from "immer";
import { useEffect, useMemo, useReducer } from "react";
import { useDispatch } from "react-redux";
import type { FCCTreeNode } from "~/features/freeCapacityPush/components/FCCTree/FCCTree";
import { isNonNull } from "~/common/utils/reducers";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { transformSrpProduct } from "~/features/search/transformers/transformSrpProduct";
import {
  type FCCSearchResultsDTO,
  type FCCTreeNodeDTO,
  fetchFCCSearchResults,
} from "~/features/freeCapacityPush/api/fetchFCCSearchResults";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { usePrevious } from "~/common/hooks/usePrevious";
import type { PaginationDTO } from "~/features/search/models/SearchResultsDTO";
import {
  type CoverageType,
  type CoverageUnit,
  fetchTrainDetails,
  type TrainDetailsData,
} from "~/features/freeCapacityPush/api/fetchTrainDetails";
import dayjs from "dayjs";
import { FccAction, trackFccAction } from "~/features/freeCapacityPush/api/trackFccAction";
import { SortDirection, type SortOption } from "~/features/search/models/models";
import { transformSorts } from "~/features/search/transformers/SearchResultDataTransformer";

export const Series = "categoryPath";
export const Hierarchy = "productHierarchy";
export const Plant = "plantHierarchy";

const INITIAL_SORT = "relevance";

export type TreeType = typeof Series | typeof Hierarchy | typeof Plant;

export interface TrainDetails extends Omit<TrainDetailsData, "salesCoverage" | "validFrom" | "validTo"> {
  validFrom?: dayjs.Dayjs;
  validTo?: dayjs.Dayjs;
}

export type SalesCoverage = TrainDetailsData["salesCoverage"];

export interface FCCState {
  initialised: boolean;
  loading: boolean;
  loadingTrain: boolean;
  error: boolean;
  data: Record<TreeType, FCCTreeNode[]>;
  expanded: Record<TreeType, string[]>;
  selectedTree: TreeType;
  selectedTreeNode: FCCTreeNode | null;
  selectedFifteenDigit: string | null;
  selectedProductCode: string | null;
  visibleTree: TreeType;
  treeFilterQuery: string;
  searchQuery: string;
  showOnlyRelevantProducts: boolean;
  pagination: PaginationDTO;
  sorts: SortOption[];
  selectedSort: string;
  selectedSortDirection?: SortDirection;
  results: Record<number, string[]>;
  searchKey?: string;
  trainDetails?: TrainDetails;
  salesCoverage?: SalesCoverage;
  salesCoverageType: CoverageType;
  salesCoverageUnit: CoverageUnit;
}

const INITIAL_STATE: FCCState = {
  initialised: false,
  loading: false,
  loadingTrain: false,
  error: false,
  data: {
    [Series]: [],
    [Hierarchy]: [],
    [Plant]: [],
  },
  expanded: {
    [Series]: [],
    [Hierarchy]: [],
    [Plant]: [],
  },
  selectedTree: Series,
  selectedTreeNode: null,
  selectedFifteenDigit: null,
  selectedProductCode: null,
  visibleTree: Series,
  treeFilterQuery: "",
  searchQuery: "",
  showOnlyRelevantProducts: false,
  pagination: {
    pageSize: 0,
    currentPage: 0,
    sort: "",
    numberOfPages: 0,
    totalNumberOfResults: 0,
  },
  sorts: [],
  selectedSort: INITIAL_SORT,
  selectedSortDirection: undefined,
  results: {},
  salesCoverageType: "CUSTOMER_CATALOG",
  salesCoverageUnit: "EURO",
};

function startSearch(newSearch = false) {
  return {
    type: "START_SEARCH",
    payload: { newSearch },
  } as const;
}
function finishSearch(data: FCCSearchResultsDTO, searchKey: string) {
  return {
    type: "FINISH_SEARCH",
    payload: { data, searchKey },
  } as const;
}

function failSearch() {
  return {
    type: "FAIL_SEARCH",
  } as const;
}

function startTrainFetch() {
  return {
    type: "START_TRAIN_FETCH",
  } as const;
}

function finishTrainFetch(trainDetails?: TrainDetails, salesCoverage?: SalesCoverage) {
  return {
    type: "FINISH_TRAIN_FETCH",
    payload: { trainDetails, salesCoverage },
  } as const;
}

function updateTreeFilterQuery(query: string) {
  return {
    type: "UPDATE_TREE_FILTER_QUERY",
    payload: query,
  } as const;
}

function clearTreeFilterQuery() {
  return {
    type: "UPDATE_TREE_FILTER_QUERY",
    payload: "",
  } as const;
}

function updateSearchQuery(query: string) {
  return {
    type: "UPDATE_SEARCH_QUERY",
    payload: query,
  } as const;
}

function clearSearchQuery() {
  return {
    type: "UPDATE_SEARCH_QUERY",
    payload: "",
  } as const;
}

function changeVisibleTree(tree: TreeType) {
  return {
    type: "CHANGE_TREE",
    payload: tree,
  } as const;
}

function selectTreeNode(node: FCCTreeNode | null, tree: TreeType) {
  return {
    type: "SELECT_TREE_NODE",
    payload: { node, tree },
  } as const;
}

function expandTreeNode(expanded: string[]) {
  return {
    type: "EXPAND_TREE_NODE",
    payload: expanded,
  } as const;
}

function toggleShowOnlyRelevantProducts() {
  return {
    type: "TOGGLE_SHOW_ONLY_RELEVANT_PRODUCTS",
  } as const;
}

function resetState() {
  return {
    type: "RESET_STATE",
  } as const;
}

function updateState(state: FCCState) {
  return {
    type: "UPDATE_STATE",
    payload: state,
  } as const;
}

function selectProduct(productCode: string | null, fifteenDigits: string | null) {
  return {
    type: "SELECT_PRODUCT",
    payload: { productCode, fifteenDigits },
  } as const;
}

function changePage(page: number) {
  return {
    type: "CHANGE_PAGE",
    payload: page,
  } as const;
}

function resetTree() {
  return {
    type: "RESET_TREE",
  } as const;
}

function updateSalesCoverageType(type: CoverageType) {
  return {
    type: "UPDATE_SALES_COVERAGE_TYPE",
    payload: type,
  } as const;
}

function updateSalesCoverageUnit(unit: CoverageUnit) {
  return {
    type: "UPDATE_SALES_COVERAGE_UNIT",
    payload: unit,
  } as const;
}
function markInitialisedAsTrue() {
  return {
    type: "MARK_INITIALISED_AS_TRUE",
  } as const;
}

function updateSort(selectedSort: string, selectedSortDirection?: SortDirection) {
  return {
    type: "updateSort",
    payload: {
      selectedSort,
      selectedSortDirection,
    },
  } as const;
}

const ACTIONS = {
  startSearch,
  finishSearch,
  failSearch,
  startTrainFetch,
  finishTrainFetch,
  selectProduct,
  updateTreeFilterQuery,
  clearTreeFilterQuery,
  updateSearchQuery,
  clearSearchQuery,
  changeVisibleTree,
  selectTreeNode,
  expandTreeNode,
  toggleShowOnlyRelevantProducts,
  resetState,
  updateState,
  changePage,
  resetTree,
  updateSalesCoverageType,
  updateSalesCoverageUnit,
  markInitialisedAsTrue,
  updateSort,
};

type ActionType = ReturnType<(typeof ACTIONS)[keyof typeof ACTIONS]>;

const REDUCER = (state: FCCState, action: ActionType): FCCState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case "START_SEARCH": {
        draft.loading = true;
        draft.error = false;
        if (action.payload.newSearch) {
          draft.data = {
            [Series]: [],
            [Hierarchy]: [],
            [Plant]: [],
          };
          if (draft.selectedTreeNode) {
            draft.selectedFifteenDigit = null;
            draft.selectedProductCode = null;
          }
        }
        break;
      }
      case "FAIL_SEARCH": {
        draft.initialised = true;
        draft.loading = false;
        draft.loadingTrain = false;
        draft.error = true;
        draft.results = {};
        break;
      }
      case "FINISH_SEARCH": {
        draft.initialised = true;
        draft.loading = false;
        const { searchKey, data } = action.payload;
        draft.pagination = data.pagination;
        draft.sorts = transformSorts(data.sorts || []);

        const { categoryPath, productHierarchy, plantHierarchy } = data;
        draft.data = {
          [Series]: transformTreeData(categoryPath),
          [Hierarchy]: transformTreeData(productHierarchy),
          [Plant]: transformTreeData(plantHierarchy),
        };
        draft.results[draft.pagination.currentPage] = data.results.map((product) => product.code);
        draft.searchKey = searchKey;

        break;
      }
      case "UPDATE_TREE_FILTER_QUERY": {
        draft.treeFilterQuery = action.payload;
        if (action.payload) {
          Object.entries(draft.data).forEach(([key, value]) => {
            draft.expanded[key as TreeType] = getAllExpandableKeys(filterTree(value, nodeMatchesQuery(action.payload)));
          });
        }
        break;
      }
      case "UPDATE_SEARCH_QUERY": {
        draft.searchQuery = action.payload;
        draft.pagination.currentPage = 0;
        break;
      }
      case "CHANGE_TREE": {
        draft.expanded[draft.visibleTree] = [];
        draft.visibleTree = action.payload;
        draft.selectedTreeNode = null;
        draft.expanded[action.payload] = [];

        draft.pagination.currentPage = 0;
        draft.selectedFifteenDigit = null;
        draft.selectedProductCode = null;
        break;
      }
      case "SELECT_TREE_NODE": {
        // If:
        // - we select a node when nothing was selected, or
        // - when we select a node in a different tree, or
        // - No node was selected, no product was selected and we select a product
        // Then:
        // - change the sales coverage values
        if (!draft.selectedTreeNode || draft.selectedTree !== action.payload.tree) {
          draft.salesCoverageUnit = "EURO";
          draft.salesCoverageType = "FCC_CATALOG_ONLY";
        }
        // If:
        // - No product was selected and we deselect tree node
        // Then:
        // - change the sales coverage values
        if (!draft.selectedProductCode && !action.payload.node) {
          draft.salesCoverageUnit = "EURO";
          draft.salesCoverageType = "CUSTOMER_CATALOG";
        }
        draft.selectedTreeNode = action.payload.node;
        draft.selectedTree = action.payload.tree;
        draft.visibleTree = action.payload.tree;
        draft.pagination.currentPage = 0;

        draft.selectedFifteenDigit = null;
        draft.selectedProductCode = null;

        if (action.payload.node) {
          const nodesToExpand = filterTree(draft.data[action.payload.tree], nodeEquals(action.payload.node));
          draft.expanded[action.payload.tree].push(...getAllExpandableKeys(nodesToExpand));
        }
        break;
      }
      case "EXPAND_TREE_NODE": {
        draft.expanded[draft.visibleTree] = action.payload;
        break;
      }
      case "TOGGLE_SHOW_ONLY_RELEVANT_PRODUCTS": {
        draft.showOnlyRelevantProducts = !draft.showOnlyRelevantProducts;
        break;
      }
      case "RESET_STATE": {
        draft.selectedTree = Series;
        draft.selectedTreeNode = null;
        draft.visibleTree = Series;
        draft.showOnlyRelevantProducts = false;
        draft.treeFilterQuery = "";
        break;
      }

      case "UPDATE_STATE": {
        if (
          draft.selectedTree !== action.payload.selectedTree ||
          draft.selectedTreeNode !== action.payload.selectedTreeNode ||
          draft.showOnlyRelevantProducts !== action.payload.showOnlyRelevantProducts
        ) {
          draft.pagination.currentPage = 0;
        }
        draft.data = action.payload.data;
        draft.selectedTree = action.payload.selectedTree;
        draft.selectedTreeNode = action.payload.selectedTreeNode;
        draft.visibleTree = action.payload.visibleTree;
        draft.showOnlyRelevantProducts = action.payload.showOnlyRelevantProducts;
        draft.treeFilterQuery = action.payload.treeFilterQuery;
        draft.salesCoverageType = action.payload.salesCoverageType;
        draft.salesCoverageUnit = action.payload.salesCoverageUnit;
        draft.results = action.payload.results;
        break;
      }

      case "SELECT_PRODUCT": {
        // If:
        // - No node was selected, no product was selected and we select a product
        // Then:
        // - change the sales coverage values
        if (!draft.selectedTreeNode && !draft.selectedProductCode) {
          draft.salesCoverageUnit = "EURO";
          draft.salesCoverageType = "FCC_CATALOG_ONLY";
        }
        // If:
        // - No node was selected and we deselect product
        // Then:
        // - change the sales coverage values
        if (!draft.selectedTreeNode && !action.payload.productCode) {
          draft.salesCoverageUnit = "EURO";
          draft.salesCoverageType = "CUSTOMER_CATALOG";
        }
        draft.selectedFifteenDigit = action.payload.fifteenDigits;
        draft.selectedProductCode = action.payload.productCode;
        break;
      }

      case "CHANGE_PAGE": {
        draft.pagination.currentPage = action.payload;
        break;
      }
      case "START_TRAIN_FETCH": {
        draft.loadingTrain = true;
        break;
      }
      case "FINISH_TRAIN_FETCH": {
        draft.trainDetails = action.payload.trainDetails;
        draft.salesCoverage = action.payload.salesCoverage;
        draft.loadingTrain = false;
        break;
      }

      case "RESET_TREE": {
        // If:
        // - No product was selected, but a node was selected
        // Then:
        // - change the sales coverage values to initial
        if (!draft.selectedProductCode && draft.selectedTreeNode) {
          draft.salesCoverageType = INITIAL_STATE["salesCoverageType"];
          draft.salesCoverageUnit = INITIAL_STATE["salesCoverageUnit"];
        }
        draft.selectedTree = INITIAL_STATE["selectedTree"];
        draft.selectedTreeNode = INITIAL_STATE["selectedTreeNode"];
        draft.visibleTree = INITIAL_STATE["visibleTree"];
        draft.treeFilterQuery = INITIAL_STATE["treeFilterQuery"];
        draft.expanded = INITIAL_STATE["expanded"];

        break;
      }

      case "UPDATE_SALES_COVERAGE_TYPE": {
        draft.salesCoverageType = action.payload;
        break;
      }
      case "UPDATE_SALES_COVERAGE_UNIT": {
        draft.salesCoverageUnit = action.payload;
        break;
      }
      case "MARK_INITIALISED_AS_TRUE": {
        draft.initialised = true;
        break;
      }
      case "updateSort": {
        draft.pagination.currentPage = 0;

        const newCode = action.payload.selectedSort;
        if (draft.selectedSort === newCode) {
          // We're just changing the sort order
          draft.selectedSortDirection = action.payload.selectedSortDirection;
        } else {
          draft.selectedSort = newCode;
          // We are assigning a new code. We need to find out if the code can be asc/dec or not
          const isAscDesc = draft.sorts.find((option) => option.code === newCode)?.isAscdesc ?? false;
          if (isAscDesc) {
            if (INITIALLY_DESCENDED_SORTS.includes(newCode)) {
              // As per SCHAEFF-30653, we need to assign desc by default for these codes
              draft.selectedSortDirection = SortDirection.DESC;
            } else {
              // Assign asc by default for a new code
              draft.selectedSortDirection = SortDirection.ASC;
            }
          } else {
            // Clear the direction since there's no asc/desc
            draft.selectedSortDirection = undefined;
          }
        }
        break;
      }
    }
  });
};

const INITIALLY_DESCENDED_SORTS = ["fccGapPieces", "fccFreeCapacityPerMonth", "fccGapEur"];

const TREE_TRACKING_ACTION = {
  [Series]: FccAction.TreeTabSeries,
  [Hierarchy]: FccAction.TreeTabHierarchy,
  [Plant]: FccAction.TreeTabPlant,
};

export const useFCCState = (isTablet = false) => {
  const [state, dispatch] = useReducer(REDUCER, INITIAL_STATE);
  const reduxDispatch = useDispatch();

  const actions = useMemo(() => {
    return {
      updateTreeFilterQuery: (query: string) => dispatch(ACTIONS.updateTreeFilterQuery(query)),
      clearTreeFilterQuery: () => dispatch(ACTIONS.clearTreeFilterQuery()),
      updateSearchQuery: (query: string) => {
        if (query.length === 1) {
          // SCHAEFF-31485 - Query should be at least two characters long to trigger a search
          return;
        }
        return dispatch(ACTIONS.updateSearchQuery(query));
      },
      clearSearchQuery: () => dispatch(ACTIONS.clearSearchQuery()),
      changeVisibleTree: (tree: TreeType) => dispatch(ACTIONS.changeVisibleTree(tree)),
      selectTreeNode: (item: FCCTreeNode | null, tree: TreeType) => {
        if (item) {
          trackFccAction({
            actionType: TREE_TRACKING_ACTION[tree],
            actionContent: item.key,
          });
        }

        return dispatch(ACTIONS.selectTreeNode(item, tree));
      },
      setExpanded: (expanded: string[]) => dispatch(ACTIONS.expandTreeNode(expanded)),
      toggleShowOnlyRelevantProducts: (value: boolean) => {
        if (value) {
          trackFccAction(FccAction.CustomerRelevant);
        }
        dispatch(ACTIONS.toggleShowOnlyRelevantProducts());
      },
      resetState: () => dispatch(ACTIONS.resetState()),
      updateState: (state: FCCState) => dispatch(ACTIONS.updateState(state)),
      selectProduct: (productCode: string | null, fifteenDigits: string | null) =>
        dispatch(ACTIONS.selectProduct(productCode, fifteenDigits)),
      changePage: (page: number) => dispatch(ACTIONS.changePage(page)),
      resetTree: () => dispatch(ACTIONS.resetTree()),
      updateSalesCoverageType: (type: CoverageType) => dispatch(ACTIONS.updateSalesCoverageType(type)),
      updateSalesCoverageUnit: (unit: CoverageUnit) => dispatch(ACTIONS.updateSalesCoverageUnit(unit)),
      updateSort: (selectedSort: string, selectedSortDirection?: SortDirection) =>
        dispatch(ACTIONS.updateSort(selectedSort, selectedSortDirection)),
    };
  }, [dispatch]);

  const { treeFilterQuery, selectedTreeNode, data } = state;

  const filteredData = useMemo(() => {
    return Object.fromEntries(
      Object.entries(data).map(([key, nodes]) => [key, filterTree(nodes, nodeMatchesQuery(treeFilterQuery))]),
    );
  }, [data, treeFilterQuery, selectedTreeNode]);

  const search = async (data: Parameters<typeof fetchFCCSearchResults>[0]) => {
    const searchKey = generateKey(data);
    if (state.searchKey === searchKey) {
      return;
    }
    dispatch(startSearch(!data.page));
    try {
      const response = await fetchFCCSearchResults(data);
      const products = response.results.map(transformSrpProduct);
      reduxDispatch(addBaseProductDataAction(products));
      dispatch(finishSearch(response, searchKey));
    } catch {
      dispatch(failSearch());
    }
  };

  const screenSize = useNumericScreenSize();
  const previousScreenSize = usePrevious(screenSize);
  useEffect(() => {
    if (screenSize <= useNumericScreenSize.phone) {
      // Page doesn't work on phones
      return;
    }
    if (state.initialised && screenSize !== previousScreenSize) {
      // If page is initialised then changing screen size does not trigger a search
      return;
    }

    search({
      query: state.searchQuery,
      [state.selectedTree]: state.selectedTreeNode?.key,
      page: state.pagination.currentPage,
      relevantProducts: state.showOnlyRelevantProducts,
      sortCode: state.selectedSort,
      sortDirection: state.selectedSortDirection,
    });
  }, [
    screenSize,
    state.searchQuery,
    state.selectedTree,
    state.selectedTreeNode,
    state.pagination.currentPage,
    state.showOnlyRelevantProducts,
    state.selectedSort,
    state.selectedSortDirection,
  ]);

  const fetchTrain = async (data: Parameters<typeof fetchTrainDetails>[0]) => {
    dispatch(startTrainFetch());
    try {
      const { salesCoverage, ...trainDTO } = await fetchTrainDetails(data);
      const trainHasAnyData = Object.values(trainDTO).some((value) => !!value);
      const train: TrainDetails | undefined = trainHasAnyData
        ? {
            ...trainDTO,
            validFrom: trainDTO.validFrom ? dayjs(trainDTO.validFrom) : undefined,
            validTo: trainDTO.validTo ? dayjs(trainDTO.validTo) : undefined,
          }
        : undefined;
      dispatch(finishTrainFetch(train, salesCoverage));
    } catch {
      dispatch(finishTrainFetch());
    }
  };

  useEffect(() => {
    if (isTablet) {
      return;
    }
    const params: Parameters<typeof fetchTrainDetails>[0] = {};
    if (state.selectedFifteenDigit) {
      params.fifteenDigit = state.selectedFifteenDigit;
    }
    if (state.selectedTree === Hierarchy && state.selectedTreeNode) {
      params.productLine = state.selectedTreeNode.key;
    } else if (state.selectedTree === Plant && state.selectedTreeNode && state.selectedTreeNode.children.length === 0) {
      // Last level of Plant hierarchy contains trains
      params.trainId = state.selectedTreeNode.key;
    }
    fetchTrain(params);
  }, [state.selectedFifteenDigit, state.selectedTree, state.selectedTreeNode]);

  return {
    state: {
      ...state,
      filteredData,
    },
    actions,
  };
};

const nodeMatchesQuery = (query: string) => (node: FCCTreeNode) => {
  return node.label?.toLowerCase().includes(query.toLowerCase()) ?? false;
};
const nodeEquals = (queryNode: FCCTreeNode) => (node: FCCTreeNode) => {
  return node.key === queryNode.key;
};

/**
 * Return a new tree where only nodes that match the query are included.
 * @param nodes
 * @param predicate
 */
function filterTree(nodes: FCCTreeNode[], predicate: (node: FCCTreeNode) => boolean): FCCTreeNode[] {
  return nodes
    .map((node) => {
      const children = filterTree(node.children, predicate);
      if (children.length > 0 || predicate(node)) {
        return { ...node, children };
      }
      return null;
    })
    .filter(isNonNull);
}

/**
 * Get keys of all nodes that have children.
 * @param nodes
 */
function getAllExpandableKeys(nodes: FCCTreeNode[]): string[] {
  return nodes
    .map((node) => {
      const children = getAllExpandableKeys(node.children);
      return node.children.length > 0 ? [node.key, ...children] : [];
    })
    .flat();
}

function transformTreeData(data: FCCTreeNodeDTO[] | null | undefined): FCCTreeNode[] {
  return (data ?? []).map((node) => ({
    key: node.code,
    label: node.name,
    quantity: node.count,
    children: transformTreeData(node.children),
  }));
}

function generateKey(data: Parameters<typeof fetchFCCSearchResults>[0]) {
  return JSON.stringify(data);
}
